import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  JURISDICTION_CODES,
  JURISDICTION_LABELS,
  recurrenceFrequencySchema,
  type CreateTemplateInput,
  type RecurrenceFrequency,
  type TemplateItemInput,
  type TemplateOutput,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { ForbiddenPage } from '../components/ForbiddenPage.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { DueDateOffsetField } from '../components/DueDateOffsetField.js';
import { LayoutTemplate, Plus, Trash2 } from 'lucide-react';

const FREQ_LABELS: Record<RecurrenceFrequency, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-annual',
  ANNUAL: 'Annual',
};

type ModalMode = { kind: 'new' } | { kind: 'edit'; template: TemplateOutput } | null;

export function TemplatesPage() {
  const { canDo } = useCapabilities();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalMode>(null);
  const [deleting, setDeleting] = useState<TemplateOutput | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canManage = canDo('templates.manage');
  const [jurFilter, setJurFilter] = useState('');

  const list = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const res = await fetch('/v1/templates', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: TemplateOutput[] } };
      return body.data.items;
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['templates'] });

  const save = useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: CreateTemplateInput }) => {
      const res = await fetch(id ? `/v1/templates/${id}` : '/v1/templates', {
        method: id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => { setModal(null); setActionError(null); refresh(); },
    onError: (err) => setActionError((err as Error).message),
  });

  const toggleActive = useMutation({
    mutationFn: async (t: TemplateOutput) => {
      const res = await fetch(`/v1/templates/${t.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ active: !t.active }),
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: refresh,
    onError: (err) => setActionError((err as Error).message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => { setDeleting(null); setActionError(null); refresh(); },
    onError: (err) => setActionError((err as Error).message),
  });

  if (!canDo('templates.view')) return <ForbiddenPage />;

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Compliance Templates</h1>
          <p className="text-sm text-muted">{list.data?.length ?? '—'} templates · spawn a client's full compliance setup in one click</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={jurFilter}
            onChange={(e) => setJurFilter(e.target.value)}
            className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          >
            <option value="">All jurisdictions</option>
            {JURISDICTION_CODES.map((j) => (
              <option key={j} value={j}>{JURISDICTION_LABELS[j as keyof typeof JURISDICTION_LABELS] ?? j}</option>
            ))}
          </select>
          {canManage && (
            <button onClick={() => setModal({ kind: 'new' })} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ New template</button>
          )}
        </div>
      </header>

      {actionError && <div className="text-sm text-rose-300">{actionError}</div>}

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-4 py-3 font-medium">Template</th>
              <th className="px-4 py-3 font-medium">Jurisdiction</th>
              <th className="px-4 py-3 font-medium">Items</th>
              <th className="px-4 py-3 font-medium">Status</th>
              {canManage && <th className="px-4 py-3 font-medium text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={5} />}
            {list.error && <tr><td colSpan={5} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>}
            {!list.isLoading && !list.error && list.data?.length === 0 && (
              <tr><td colSpan={5}><EmptyState icon={LayoutTemplate} title="No templates yet." /></td></tr>
            )}
            {!list.isLoading && !list.error && (list.data?.length ?? 0) > 0 && list.data!.filter((t) => !jurFilter || t.jurisdiction === jurFilter).length === 0 && (
              <tr><td colSpan={5}><EmptyState icon={LayoutTemplate} title={`No templates for ${JURISDICTION_LABELS[jurFilter as keyof typeof JURISDICTION_LABELS] ?? jurFilter} yet — create one with “+ New template”.`} /></td></tr>
            )}
            {list.data?.filter((t) => !jurFilter || t.jurisdiction === jurFilter).map((t) => (
              <tr key={t.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3">
                  <div className="text-white">{t.name}</div>
                  {t.description && <div className="text-xs text-muted mt-0.5 max-w-md truncate">{t.description}</div>}
                </td>
                <td className="px-4 py-3 text-muted">{JURISDICTION_LABELS[t.jurisdiction as keyof typeof JURISDICTION_LABELS] ?? t.jurisdiction}</td>
                <td className="px-4 py-3 text-muted">{t.items.length}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${t.active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-bg-hover text-muted'}`}>
                    {t.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {canManage && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      <EditIcon onClick={() => setModal({ kind: 'edit', template: t })} label="Edit template" />
                      <button onClick={() => toggleActive.mutate(t)} className="text-xs text-muted hover:text-white px-1">{t.active ? 'Deactivate' : 'Activate'}</button>
                      <DeleteIcon onClick={() => setDeleting(t)} label="Delete template" />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <TemplateModal
          mode={modal}
          onClose={() => setModal(null)}
          onSave={(id, input) => save.mutate({ id, input })}
          isPending={save.isPending}
          error={save.error?.message ?? null}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl animate-pop p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Delete template</h2>
              <button onClick={() => setDeleting(null)} className="text-muted hover:text-white">✕</button>
            </div>
            <p className="text-sm text-white">Delete &ldquo;{deleting.name}&rdquo;? Compliance items already spawned from it are not affected.</p>
            {remove.error && <p className="text-xs text-rose-400 mt-2">{(remove.error as Error).message}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setDeleting(null)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
              <button onClick={() => remove.mutate(deleting.id)} disabled={remove.isPending} className="bg-rose-700 hover:bg-rose-600 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg">
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateModal({ mode, onClose, onSave, isPending, error }: {
  mode: { kind: 'new' } | { kind: 'edit'; template: TemplateOutput };
  onClose: () => void;
  onSave: (id: string | undefined, input: CreateTemplateInput) => void;
  isPending: boolean;
  error: string | null;
}) {
  const isEdit = mode.kind === 'edit';
  const t = isEdit ? mode.template : null;
  const [name, setName] = useState(t?.name ?? '');
  const [jurisdiction, setJurisdiction] = useState(t?.jurisdiction ?? 'UAE');
  const [description, setDescription] = useState(t?.description ?? '');
  const [items, setItems] = useState<TemplateItemInput[]>(
    t?.items.map((i) => ({ serviceType: i.serviceType, title: i.title, recurrenceFrequency: i.recurrenceFrequency, ordering: i.ordering, notes: i.notes ?? undefined, defaultIncluded: i.defaultIncluded, dueDateOffsetDays: i.dueDateOffsetDays ?? null }))
      ?? [{ serviceType: '', title: '', recurrenceFrequency: 'MONTHLY', ordering: 1, defaultIncluded: true, dueDateOffsetDays: null }],
  );

  function updateItem<K extends keyof TemplateItemInput>(idx: number, k: K, v: TemplateItemInput[K]) {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave(t?.id, {
      name: name.trim(),
      jurisdiction,
      description: description.trim() || null,
      active: t?.active ?? true,
      items: items.map((it, idx) => ({ ...it, ordering: idx + 1, notes: it.notes?.trim() || null })),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-2xl mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{isEdit ? 'Edit template' : 'New template'}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">Name <span className="text-rose-400">*</span></label>
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" placeholder="UAE — Bookkeeping + VAT + …" />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Jurisdiction <span className="text-rose-400">*</span></label>
              <select value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">
                {JURISDICTION_CODES.map((j) => <option key={j} value={j}>{JURISDICTION_LABELS[j as keyof typeof JURISDICTION_LABELS] ?? j}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={2} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-muted">Items (spawned in this order) <span className="text-rose-400">*</span></label>
              <button type="button" onClick={() => setItems((arr) => [...arr, { serviceType: '', title: '', recurrenceFrequency: 'MONTHLY', ordering: arr.length + 1, defaultIncluded: false, dueDateOffsetDays: null }])} className="text-xs text-accent hover:underline inline-flex items-center gap-1">
                <Plus size={12} /> Add item
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-bg-base border border-border rounded-lg p-2">
                  <input
                    value={it.serviceType}
                    onChange={(e) => updateItem(idx, 'serviceType', e.target.value)}
                    maxLength={100} required placeholder="Service type (e.g. Bookkeeping)"
                    className="col-span-4 bg-bg-panel border border-border rounded-lg px-2 py-1.5 text-xs text-white"
                  />
                  <input
                    value={it.title}
                    onChange={(e) => updateItem(idx, 'title', e.target.value)}
                    maxLength={200} required placeholder="Title (e.g. Monthly bookkeeping)"
                    className="col-span-3 bg-bg-panel border border-border rounded-lg px-2 py-1.5 text-xs text-white"
                  />
                  <select
                    value={it.recurrenceFrequency}
                    onChange={(e) => updateItem(idx, 'recurrenceFrequency', e.target.value as RecurrenceFrequency)}
                    className="col-span-2 bg-bg-panel border border-border rounded-lg px-2 py-1.5 text-xs text-white"
                  >
                    {recurrenceFrequencySchema.options.map((f) => <option key={f} value={f}>{FREQ_LABELS[f]}</option>)}
                  </select>
                  <label className="col-span-2 flex items-center gap-1 text-[10px] text-muted cursor-pointer py-1.5" title="Pre-checked in the apply modal">
                    <input
                      type="checkbox"
                      checked={it.defaultIncluded}
                      onChange={(e) => updateItem(idx, 'defaultIncluded', e.target.checked)}
                      className="accent-emerald-400"
                    />
                    Default
                  </label>
                  <button
                    type="button"
                    onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}
                    disabled={items.length <= 1}
                    className="col-span-1 text-muted hover:text-rose-400 disabled:opacity-30 py-1.5 flex justify-center"
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="col-span-12 sm:col-span-6">
                    <DueDateOffsetField
                      value={it.dueDateOffsetDays ?? null}
                      onChange={(v) => updateItem(idx, 'dueDateOffsetDays', v)}
                      label="Due date anchor (optional)"
                      required={false}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted mt-1">serviceType lands verbatim on spawned items AND feeds the compliance service-type pickers — a new service type here becomes selectable firm-wide for this jurisdiction, no deploy needed. The due date anchor is days after the engagement end date (deadline chosen at apply time).</p>
          </div>

          {error && <p className="text-xs text-rose-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button type="submit" disabled={isPending} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
              {isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
